import { createRouter, createWebHistory } from '@ionic/vue-router'
import { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import TabsPage from '@/views/TabsPage.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/tabs/dashboard',
  },
  {
    path: '/auth',
    redirect: '/auth/login',
    meta: { guest: true },
  },
  {
    path: '/auth/login',
    name: 'login',
    component: () => import('@/views/Auth/LoginPage.vue'),
    meta: { guest: true },
  },
  {
    path: '/auth/register',
    name: 'register',
    component: () => import('@/views/Auth/RegisterPage.vue'),
    meta: { guest: true },
  },
  {
    path: '/auth/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/Auth/ForgotPasswordPage.vue'),
    meta: { guest: true },
  },
  {
    path: '/auth/verify-email',
    name: 'verify-email',
    component: () => import('@/views/Auth/VerifyEmailPage.vue'),
    meta: { requiresAuth: true, skipVerification: true },
  },
  {
    path: '/tabs/',
    component: TabsPage,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/tabs/dashboard',
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/Dashboard/DashboardPage.vue'),
      },
      {
        path: 'health-records',
        name: 'health-records',
        component: () => import('@/views/HealthRecords/HealthRecordsPage.vue'),
      },
      {
        path: 'health-records/add',
        name: 'add-health-record',
        component: () => import('@/views/HealthRecords/AddHealthRecordPage.vue'),
      },
      {
        path: 'health-records/:id',
        name: 'view-health-record',
        component: () => import('@/views/HealthRecords/ViewHealthRecordPage.vue'),
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/views/Reports/ReportsPage.vue'),
      },
      {
        path: 'reports/test-reports',
        name: 'test-reports',
        component: () => import('@/views/Reports/TestReportsPage.vue'),
      },
      {
        path: 'reports/prescriptions',
        name: 'prescriptions',
        component: () => import('@/views/Reports/PrescriptionsPage.vue'),
      },
      {
        path: 'medications',
        name: 'medications',
        component: () => import('@/views/Medications/MedicationsPage.vue'),
      },
      {
        path: 'medications/add',
        name: 'add-medication',
        component: () => import('@/views/Medications/AddMedicationPage.vue'),
      },
      {
        path: 'medications/purchase',
        name: 'medicine-purchase',
        component: () => import('@/views/Medications/MedicinePurchasePage.vue'),
      },
      {
        path: 'medications/:id/edit',
        name: 'edit-medication',
        component: () => import('@/views/Medications/AddMedicationPage.vue'),
      },
      {
        path: 'medications/:id/history',
        name: 'medication-history',
        component: () => import('@/views/Medications/MedicationHistoryPage.vue'),
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/Settings/SettingsPage.vue'),
      },
      {
        path: 'settings/profile',
        name: 'profile',
        component: () => import('@/views/Settings/ProfilePage.vue'),
      },
      {
        path: 'settings/dependents',
        name: 'dependents',
        component: () => import('@/views/Settings/DependentsPage.vue'),
      },
      {
        path: 'settings/sharing',
        name: 'sharing',
        component: () => import('@/views/Settings/SharingPage.vue'),
      },
      {
        path: 'settings/notifications',
        name: 'notifications-settings',
        component: () => import('@/views/Settings/NotificationsSettingsPage.vue'),
      },
      {
        path: 'settings/metrics',
        name: 'metrics-settings',
        component: () => import('@/views/Settings/MetricsSettingsPage.vue'),
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // If we have a token but no user data, try to fetch the user
  if (authStore.token && !authStore.user) {
    try {
      await authStore.fetchUser()
    } catch (error) {
      // Token is invalid, clear it
      authStore.logout()
    }
  }

  const isAuthenticated = authStore.isAuthenticated
  const isEmailVerified = authStore.isEmailVerified

  // If route requires auth and user is not authenticated
  if (to.meta.requiresAuth && !isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }

  // If route is for guests only and user is authenticated
  if (to.meta.guest && isAuthenticated) {
    // If authenticated but not verified, go to verify page instead
    if (!isEmailVerified) {
      next({ name: 'verify-email' })
      return
    }
    next({ name: 'dashboard' })
    return
  }

  // If authenticated but email not verified, redirect to verify page
  // unless the route allows skipping verification
  if (isAuthenticated && !isEmailVerified && !to.meta.skipVerification && to.name !== 'verify-email') {
    next({ name: 'verify-email' })
    return
  }

  // If already verified and trying to access verify page, redirect to dashboard
  if (isAuthenticated && isEmailVerified && to.name === 'verify-email') {
    next({ name: 'dashboard' })
    return
  }

  next()
})

export default router
